import { useTheme } from '@mui/material/styles';
import background from '../../assets/background.json';
import cornersData from '../../assets/cornersData.json';
import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import {
    Point,
    VictoryArea,
    VictoryAxis,
    VictoryChart,
    VictoryLabel,
    VictoryLine,
    VictoryScatter
} from 'victory';
import PropTypes from 'prop-types';

/** @function
 * Helpers for creating event handlers for the scatter plot.
 *
 * This is required to handle javascript's painful type system.
 * Handlers are bound to additional provided argument, graphRef.
 *
 * @param onMouseOver {function(event, data, graphRef): Array<Object>}
 * @param onMouseOut {function(): Array<Object>}
 * @param onClick {function(): Array<Object>}
 * @param graphRef {Ref}
 * @returns {{Object}}
 */
function createEventHandlers({ onMouseOver, onMouseOut, onClick }, graphRef) {
    let eventHandlers = {};

    if (onMouseOut) {
        eventHandlers.onMouseOut = onMouseOut;
    }

    if (onMouseOver) {
        eventHandlers.onMouseOver = (event, data) =>
            onMouseOver(event, data, graphRef);
    }
    if (onClick) {
        eventHandlers.onClick = (event, data) => onClick(event, data, graphRef);
    }
    return eventHandlers;
}

/**
 * Renders a scatter plot with customisable data points.
 * It displays data points on a two-dimensional graph.
 *
 * @component
 * @param {array} data - the array containing the data to plot
 * @param {string} dataTestId - the test ID for the component
 * @param {string} dataAnalyticsId - To identify the component via our analytics third party
 * @param {Function} onMouseOver - function to handle mouse over events on data points
 * @param {Function} onMouseOut - function to handle mouse out events on data points
 * @param {Function} onClick - function to handle click events on data points
 * @param {array} externalDotMutations - array of mutations to apply to data points
 * @param {Number} size - Dot initial size
 * @param {Object} style - Dot and label styling
 * @param {Function} labels - function to generate labels for data points
 * @param {node} labelComponent - custom label component for data points
 * @param {Object} selectedDot - currently selected dot
 * @returns {JSX.Element}
 */
const Scatter = ({
    data,
    dataTestId,
    dataAnalyticsId,
    onMouseOver,
    onMouseOut,
    onClick,
    externalDotMutations,
    size,
    style,
    labels,
    labelComponent,
    selectedDot
}) => {
    const theme = useTheme();

    const colors = theme.palette['scatterPlot'].background;
    const graphRef = useRef();

    let eventHandlers = createEventHandlers(
        { onMouseOver, onMouseOut, onClick },
        graphRef
    );

    return (
        <Box
            ref={graphRef}
            data-testid={dataTestId}
            data-analyticsid={dataAnalyticsId}
            sx={{ paddingTop: 0.5 }}
        >
            <VictoryChart
                height={theme.sizing.scatterPlot.height}
                width={theme.sizing.scatterPlot.width}
                padding={{
                    top: theme.sizing.scatterPlot.margin.top,
                    bottom: theme.sizing.scatterPlot.margin.bottom,
                    left: theme.sizing.scatterPlot.margin.left,
                    right: theme.sizing.scatterPlot.margin.right
                }}
                domain={{ x: [-0.002, 1.002], y: [-0.002, 1.002] }}
            >
                {/*Render the x axis*/}
                <VictoryAxis
                    tickValues={[]}
                    label='Decision Hit Rate'
                    axisLabelComponent={
                        <VictoryLabel
                            dy={theme.sizing.scatterPlot.axes.labelToChartDistX}
                        />
                    }
                    style={{
                        axisLabel: {
                            fontSize: theme.sizing.scatterPlot.axes.fontSize,
                            fontFamily: theme.typography.fontFamily
                        },
                        tickLabels: {
                            fontFamily: theme.typography.fontFamily
                        },
                        axis: {
                            strokeWidth: 0
                        }
                    }}
                />
                {/*Render the y axis*/}
                <VictoryAxis
                    dependentAxis
                    tickValues={[]}
                    label='Decision Payoff Ratio (Scaled)'
                    axisLabelComponent={
                        <VictoryLabel
                            dy={theme.sizing.scatterPlot.axes.labelToChartDistY}
                        />
                    }
                    style={{
                        axisLabel: {
                            fontSize: theme.sizing.scatterPlot.axes.fontSize,
                            fontFamily: theme.typography.fontFamily
                        },
                        tickLabels: {
                            fontFamily: theme.typography.fontFamily
                        },
                        axis: {
                            strokeWidth: 0
                        }
                    }}
                />
                {/*Render background, using the background data array of points.*/}
                {colors.map((color, i) => (
                    <VictoryArea
                        key={color}
                        data={background}
                        x={'x'}
                        y={'y' + Math.abs(i - 5)}
                        style={{ data: { fill: color, strokeWidth: 0 } }}
                    />
                ))}
                {/*Render the zero line */}
                <VictoryLine
                    data={background}
                    x={'x'}
                    y={'y3'}
                    style={{
                        data: {
                            stroke: theme.palette.zeroLine.stroke,
                            strokeWidth: 1,
                            strokeLinecap: 'round',
                            strokeDasharray: 3
                        }
                    }}
                />
                {/*Rounded corners!*/}
                {cornersData.map((corner) => (
                    <VictoryArea
                        key={corner}
                        data={corner}
                        interpolation='basis'
                        style={{ data: { fill: 'white', strokeWidth: 0 } }}
                    />
                ))}
                {/* Render the dots! */}
                <VictoryScatter
                    data={data}
                    labels={labels}
                    labelComponent={labelComponent}
                    dataComponent={<Point role='dot' ariaLabel={dataTestId} />}
                    sortKey={'victoryOrdering'}
                    x={'hitRate'}
                    y={'payoffTransformed'}
                    size={size}
                    style={style}
                    events={[
                        {
                            target: 'data',
                            eventHandlers: eventHandlers
                        }
                    ]}
                    externalEventMutations={externalDotMutations}
                />
                {/* Render any selected dot (from row or dot click events) after all other dots,
                bringing it to the front of the chart. Apply separate styling to the selected dot.
                This approach eliminates the need to re-order the data array or modify individual dots within the array. */}
                {selectedDot && (
                    <VictoryScatter
                        data={[selectedDot.row]}
                        dataComponent={
                            <Point role='dot' ariaLabel={dataTestId} />
                        }
                        x={'hitRate'}
                        y={'payoffTransformed'}
                        size={
                            theme.sizing.scatterPlot.frontierPoints.radius
                                .enlarged
                        }
                        style={{
                            data: {
                                stroke: selectedDot.isClicked
                                    ? theme.palette.scatterPlot.frontierPoints
                                          .stroke.initial
                                    : theme.palette.scatterPlot.frontierPoints
                                          .stroke.hover,
                                strokeWidth:
                                    theme.sizing.scatterPlot.frontierPoints
                                        .stroke.enlarged,
                                fill: ({ datum }) => datum.dotColor
                            }
                        }}
                    />
                )}
            </VictoryChart>
        </Box>
    );
};

Scatter.propTypes = {
    data: PropTypes.array,
    dataTestId: PropTypes.string,
    onMouseOver: PropTypes.func,
    onMouseOut: PropTypes.func,
    onClick: PropTypes.func,
    externalDotMutations: PropTypes.array,
    size: PropTypes.number,
    style: PropTypes.object,
    labels: PropTypes.func,
    labelComponent: PropTypes.node,
    selectedDot: PropTypes.object,
    dataAnalyticsId: PropTypes.string
};

export default Scatter;
